import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Equipment {
  id: number
  display_name: string
  requires_odometer: boolean
}

interface Assignment {
  id: number
  assigned_at: string
  returned_at: string | null
  employee_name: string | null
  removed_by_name: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  notes: string | null
  currently_assigned: boolean
}

interface Props {
  equipment: Equipment
  assignments: Assignment[]
  can_assign?: boolean
}

export default function AssignmentHistory({
  equipment,
  assignments,
  can_assign,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Equipment", href: "/equipment" },
    { title: equipment.display_name, href: `/equipment/${equipment.id}` },
    {
      title: "Assignment History",
      href: `/equipment/${equipment.id}/assignment_history`,
    },
  ]

  const formatDateTime = (value: string | null) => {
    if (!value) return "-"
    return new Date(value).toLocaleString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const formatOdometer = (value: number | null) => {
    if (value == null || value === 0) return "?"
    return Math.round(value).toLocaleString()
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${equipment.display_name} - Assignment History`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Assignment History</h1>
          <div className="space-x-2">
            {can_assign && (
              <Link
                href={`/equipment/${equipment.id}/assign`}
                className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
              >
                Assign to Employee
              </Link>
            )}
            <Link
              href={`/equipment/${equipment.id}`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              &larr; {equipment.display_name}
            </Link>
          </div>
        </div>

        {assignments.length > 0 ? (
          <ul className="space-y-3">
            {assignments.map((assignment) => {
              const hasOdometer =
                equipment.requires_odometer ||
                (assignment.start_odometer != null &&
                  assignment.start_odometer > 0) ||
                (assignment.end_odometer != null && assignment.end_odometer > 0)

              return (
                <li
                  key={assignment.id}
                  className="border-hpi-light-gray rounded border p-4"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {assignment.employee_name ?? "Unknown"}
                      </span>
                      {assignment.currently_assigned && (
                        <span className="bg-hpi-dark-green rounded px-2 py-0.5 text-xs text-white">
                          Current
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {assignment.currently_assigned && can_assign && (
                        <Link
                          href={`/equipment/${equipment.id}/return_assignment/${assignment.id}`}
                          className="bg-hpi-gold hover:bg-hpi-gold/80 rounded px-3 py-1 text-sm text-white"
                        >
                          Return
                        </Link>
                      )}
                      {can_assign && (
                        <Link
                          href={`/equipment/${equipment.id}/edit_assignment/${assignment.id}`}
                          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-3 py-1 text-sm text-white"
                        >
                          Edit
                        </Link>
                      )}
                      {can_assign && (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              confirm(
                                "Are you sure you want to delete this assignment?",
                              )
                            ) {
                              router.delete(
                                `/equipment/${equipment.id}/assignments/${assignment.id}`,
                              )
                            }
                          }}
                          className="bg-hpi-red hover:bg-hpi-red/80 rounded px-3 py-1 text-sm text-white"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1 text-sm">
                    <div>
                      <span className="font-medium">Assigned:</span>{" "}
                      {formatDateTime(assignment.assigned_at)}
                    </div>

                    {assignment.returned_at && (
                      <div>
                        <span className="font-medium">Returned:</span>{" "}
                        {formatDateTime(assignment.returned_at)}
                        {assignment.removed_by_name && (
                          <span> by {assignment.removed_by_name}</span>
                        )}
                      </div>
                    )}

                    {hasOdometer && (
                      <div>
                        <span className="font-medium">Odo/Hours:</span>{" "}
                        {formatOdometer(assignment.start_odometer)}-
                        {formatOdometer(assignment.end_odometer)}
                        {assignment.odometer_usage != null && (
                          <span>
                            {" "}
                            ({Math.round(assignment.odometer_usage)})
                          </span>
                        )}
                      </div>
                    )}

                    {assignment.notes && (
                      <div className="text-hpi-med-gray">
                        <span className="font-medium">Notes:</span>{" "}
                        {assignment.notes}
                      </div>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <div className="bg-hpi-lightest-gray text-hpi-med-gray rounded p-4 text-center">
            No assignment history found
          </div>
        )}
      </div>
    </AppLayout>
  )
}
